import { Loading03Icon } from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  IncomeStatus,
  ReverifyRequest,
  VerificationStatus
} from "shared/types";
import { CombinedIncome } from "../types/income";
import { Card } from "shared/components/Card";
import {
  getLatestReverifyRequestForIncome,
  getReverifyDetails
} from "../slices/ReverifySlice/utils";

interface Props {
  incomes: CombinedIncome[];
  pendingReverifyRequests?: ReverifyRequest[];
  completedReverifyRequests?: ReverifyRequest[];
  verificationStatus?: VerificationStatus;
}

export const ReverifyGlobalWarning = ({
  incomes,
  pendingReverifyRequests = [],
  completedReverifyRequests = [],
  verificationStatus
}: Props) => {
  if (!pendingReverifyRequests.length && !completedReverifyRequests.length)
    return null;

  // In future, we may need to detect & display multiple reverifyRequests
  // simultaneously but for now displaying just the 1st found is fine.
  const { reverifyingIncome, reverifyRequest } = incomes.reduce<{
    reverifyingIncome: CombinedIncome | null;
    reverifyRequest: ReverifyRequest | null;
  }>(
    (acc, income) => {
      if (acc.reverifyRequest) return acc;

      const pendingMatch = getLatestReverifyRequestForIncome(
        income.id,
        pendingReverifyRequests
      );
      if (pendingMatch)
        return { reverifyingIncome: income, reverifyRequest: pendingMatch };

      const completedMatch =
        income.status === IncomeStatus.Processing
          ? getLatestReverifyRequestForIncome(
              income.id,
              completedReverifyRequests
            )
          : null;
      if (completedMatch)
        return { reverifyingIncome: income, reverifyRequest: completedMatch };

      return acc;
    },
    { reverifyingIncome: null, reverifyRequest: null }
  );

  if (!reverifyingIncome) return null;

  const { initiatedBy, formattedDate, incomeName, title } = getReverifyDetails(
    reverifyingIncome,
    reverifyRequest,
    verificationStatus
  );

  return (
    <Card className="w-full py-4 px-2 mt-4 mb-3 space-y-1">
      <div className="flex items-center gap-3">
        <HugeiconsIcon
          icon={Loading03Icon}
          className="text-warning-600"
          size={20}
        />
        <p className="font-bold text-md text-content-1000">{title}</p>
      </div>
      <p className="font-medium text-sm text-content-500 pl-8">
        Re-verification was initiated by {initiatedBy} on {formattedDate}
        <br />
        Income source: {incomeName}
      </p>
    </Card>
  );
};
